/**
 * Dify API 服务配置和调用函数
 */

// Dify API 配置
const DIFY_CONFIG = {
  baseURL: process.env.REACT_APP_DIFY_BASE_URL || 'https://api.dify.ai/v1',
  apiKey: process.env.REACT_APP_DIFY_API_KEY || ''
};

// 防恶意刷机制
class RateLimiter {
  private requestHistory: Map<string, number[]> = new Map();
  private messageHistory: Set<string> = new Set();
  private readonly maxRequestsPerMinute = 10;
  private readonly duplicateCheckWindow = 30000; // 30秒内不允许重复消息

  /**
   * 检查请求频率限制
   */
  checkRateLimit(userId: string): boolean {
    const now = Date.now();
    const userRequests = this.requestHistory.get(userId) || [];
    
    // 清理1分钟前的请求记录
    const recentRequests = userRequests.filter(time => now - time < 60000);
    
    if (recentRequests.length >= this.maxRequestsPerMinute) {
      return false;
    }
    
    recentRequests.push(now);
    this.requestHistory.set(userId, recentRequests);
    return true;
  }

  /**
   * 检查重复消息
   */
  checkDuplicateMessage(message: string): boolean {
    const messageKey = message.trim().toLowerCase();
    
    if (this.messageHistory.has(messageKey)) {
      return false; // 重复消息
    }
    
    this.messageHistory.add(messageKey);
    
    // 30秒后清理消息记录
    setTimeout(() => {
      this.messageHistory.delete(messageKey);
    }, this.duplicateCheckWindow);
    
    return true;
  }

  /**
   * 验证消息内容
   */
  validateMessage(message: string): { valid: boolean; error?: string } {
    if (!message || message.trim().length === 0) {
      return { valid: false, error: '消息不能为空' };
    }
    
    if (message.length > 500) {
      return { valid: false, error: '消息长度不能超过500个字符' };
    }
    
    // 检查是否包含恶意内容（简单示例）
    const maliciousPatterns = [/script/i, /javascript/i, /eval/i, /alert/i];
    for (const pattern of maliciousPatterns) {
      if (pattern.test(message)) {
        return { valid: false, error: '消息包含不允许的内容' };
      }
    }
    
    return { valid: true };
  }
}

const rateLimiter = new RateLimiter();


// 消息类型定义
interface DifyMessage {
  query: string;
  conversation_id?: string | null;
  user: string;
  inputs?: Record<string, any>;
}

interface DifyResponse {
  answer: string;
  conversation_id: string;
  message_id: string;
  metadata?: Record<string, any>;
}

/**
 * 调用Dify聊天API（流式模式）
 */
export const callDifyChat = async (message: DifyMessage): Promise<DifyResponse> => {
  try {
    // 验证消息内容
    const validation = rateLimiter.validateMessage(message.query);
    if (!validation.valid) {
      throw new Error(validation.error);
    }

    // 检查请求频率限制
    if (!rateLimiter.checkRateLimit(message.user)) {
      throw new Error('请求过于频繁，请稍后再试');
    }

    // 注释掉重复消息检查，因为可能误判正常对话
    // if (!rateLimiter.checkDuplicateMessage(message.query)) {
    //   throw new Error('请不要发送重复的消息');
    // }

    const requestBody: any = {
      inputs: message.inputs || {},
      query: message.query,
      response_mode: 'streaming',
      user: message.user
    };
    
    // 只有当conversation_id存在且不为空时才添加到请求体中
    if (message.conversation_id && typeof message.conversation_id === 'string' && message.conversation_id.trim() !== '') {
      requestBody.conversation_id = message.conversation_id;
      console.log('使用现有会话ID:', message.conversation_id);
    } else {
      console.log('开始新会话');
    }

    console.log('发送请求到Dify API:', {
      url: `${DIFY_CONFIG.baseURL}/chat-messages`,
      body: requestBody
    });

    const response = await fetch(`${DIFY_CONFIG.baseURL}/chat-messages`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${DIFY_CONFIG.apiKey}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody)
    });

    console.log('Dify API响应状态:', response.status);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Dify API错误响应:', errorText);
      throw new Error(`Dify API错误: ${response.status} - ${errorText}`);
    }

    // 处理流式响应
    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error('无法获取响应流');
    }

    let fullAnswer = '';
    let conversationId = '';
    let messageId = '';
    let metadata = {};
    let eventCount = 0;

    const decoder = new TextDecoder();
    let buffer = '';
    
    console.log('开始处理Dify流式响应...');
    
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      
      const chunk = decoder.decode(value, { stream: true });
      buffer += chunk;
      const lines = buffer.split('\n');
      
      // 保留最后一行（可能不完整）
      buffer = lines.pop() || '';
      
      for (const line of lines) {
        if (line.startsWith('data: ')) {
          try {
            const jsonStr = line.slice(6).trim();
            
            if (jsonStr === '[DONE]' || jsonStr === '') continue;
            
            const data = JSON.parse(jsonStr);
            eventCount++;
            // 只记录关键事件，减少日志输出
            if (data.event === 'message_end' || data.event === 'error') {
              console.log(`关键事件 ${eventCount}:`, data.event, data);
            }
            
            // 处理不同类型的事件
            if (data.event === 'agent_message') {
              const answerPart = data.answer || '';
              if (answerPart) {
                fullAnswer += answerPart;
              }
            } else if (data.event === 'message_end') {
              console.log('消息结束，获取会话信息:', {
                conversation_id: data.conversation_id,
                message_id: data.message_id,
                id: data.id
              });
              conversationId = data.conversation_id || '';
              messageId = data.message_id || data.id || '';
              metadata = data.metadata || {};
            } else if (data.event === 'message_replace') {
              // 处理消息替换事件
              fullAnswer = data.answer || fullAnswer;
            } else if (data.event === 'agent_thought') {
              // 处理agent思考过程，记录但不显示给用户
              // 静默处理，不输出日志
            } else if (data.event === 'message_file') {
              // 处理文件消息
              // 静默处理，不输出日志
            } else if (data.event === 'tts_message' || data.event === 'tts_message_end') {
              // 处理TTS相关事件
              // 静默处理，不输出日志
            }
          } catch (e) {
            console.warn('解析流式数据失败:', line, e);
            continue;
          }
        }
      }
    }
    
    console.log(`Dify响应处理完成，共 ${eventCount} 个事件，最终内容长度: ${fullAnswer.length}`);

    const result = {
      answer: fullAnswer || '抱歉，我无法理解您的问题。',
      conversation_id: conversationId,
      message_id: messageId,
      metadata
    };

    console.log('Dify API最终结果:', result);
    return result;
  } catch (error) {
    console.error('Dify API调用失败:', error);
    throw error;
  }
};

/**
 * 获取会话历史
 */
export const getDifyConversationHistory = async (conversationId: string, user: string) => {
  try {
    const response = await fetch(`${DIFY_CONFIG.baseURL}/messages?conversation_id=${conversationId}&user=${user}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${DIFY_CONFIG.apiKey}`,
        'Content-Type': 'application/json',
      }
    });

    if (!response.ok) {
      throw new Error(`获取会话历史失败: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('获取会话历史失败:', error);
    throw error;
  }
};